/**
 * Il riciclo delle righe, sui due assi.
 *
 * «Disegnare dodicimila righe non è lento: è impossibile. Solo le visibili esistono»
 * (07-PRESTAZIONI §3). Questi conti stanno qui e non dentro un componente perché sono la
 * cosa che la fase 1 deve dimostrare, e una prova che gira in Node vale più di
 * un'impressione davanti a uno schermo.
 */

export interface VisibleRange {
  /** Primo indice da disegnare, margine compreso. */
  readonly firstIndex: number;
  /** Indice successivo all'ultimo da disegnare. */
  readonly endIndex: number;
  /** Quanti elementi il chiamante deve tenere nel DOM. */
  readonly count: number;
  /** Lo spostamento in pixel del primo elemento disegnato rispetto all'origine. */
  readonly offsetPx: number;
}

/**
 * La finestra di indici da disegnare per un asse a passo fisso.
 *
 * `overscan` è il margine: righe disegnate fuori campo perché la freccia successiva non
 * debba aspettare un disegno. Va tenuto piccolo — è memoria, ed è la memoria che su una
 * Fire TV Stick fa chiudere l'app in sottofondo.
 */
export function computeVisibleRange(
  scrollPx: number,
  viewportPx: number,
  itemPitchPx: number,
  totalItems: number,
  overscan: number,
): VisibleRange {
  if (totalItems <= 0 || itemPitchPx <= 0 || viewportPx <= 0) {
    return { firstIndex: 0, endIndex: 0, count: 0, offsetPx: 0 };
  }
  const clampedScroll = Math.max(0, Math.min(scrollPx, Math.max(0, totalItems * itemPitchPx - viewportPx)));
  const firstVisible = Math.floor(clampedScroll / itemPitchPx);
  const lastVisible = Math.ceil((clampedScroll + viewportPx) / itemPitchPx);
  const firstIndex = Math.max(0, firstVisible - overscan);
  const endIndex = Math.min(totalItems, lastVisible + overscan);
  return {
    firstIndex,
    endIndex,
    count: Math.max(0, endIndex - firstIndex),
    offsetPx: firstIndex * itemPitchPx,
  };
}

/**
 * Lo scorrimento minimo perché l'indice `index` stia dentro il campo con un margine.
 *
 * Minimo di proposito: se l'elemento è già visibile con il suo margine, lo scorrimento
 * non cambia e non c'è niente da ridisegnare. È metà della ragione per cui il fuoco si
 * sposta senza che la griglia si rifaccia.
 */
export function scrollToReveal(
  index: number,
  scrollPx: number,
  viewportPx: number,
  itemPitchPx: number,
  totalItems: number,
  marginPx: number,
): number {
  const maxScroll = Math.max(0, totalItems * itemPitchPx - viewportPx);
  const itemStart = index * itemPitchPx;
  const itemEnd = itemStart + itemPitchPx;
  let next = scrollPx;
  if (itemStart - marginPx < scrollPx) next = itemStart - marginPx;
  else if (itemEnd + marginPx > scrollPx + viewportPx) next = itemEnd + marginPx - viewportPx;
  return Math.max(0, Math.min(next, maxScroll));
}

/**
 * Lo stesso, per un asse a passo variabile: i programmi sulla riga del tempo non sono
 * larghi uguale, quindi l'indice non basta e serve il rettangolo.
 */
export function scrollToRevealSpan(
  spanStartPx: number,
  spanWidthPx: number,
  scrollPx: number,
  viewportPx: number,
  contentWidthPx: number,
  marginPx: number,
): number {
  const maxScroll = Math.max(0, contentWidthPx - viewportPx);
  let next = scrollPx;
  if (spanStartPx - marginPx < scrollPx) next = spanStartPx - marginPx;
  else if (spanStartPx + spanWidthPx + marginPx > scrollPx + viewportPx) {
    next = spanStartPx + spanWidthPx + marginPx - viewportPx;
  }
  return Math.max(0, Math.min(next, maxScroll));
}
